import { useState, useEffect } from "react";
import { ExpandedContent, Separator, TableContainer } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import LogsVazio from "../../Compartilhado/LogsVazio";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

interface CampoAlterado {
    campo: string;
    antes: any;
    depois: any;
}

const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
        'Pendente': 'Pendente',
        'Em Triagem': 'Em Triagem',
        'Em Contratação': 'Em Contratação',
        'Aguardando Execução': 'Aguardando Execução',
        'Em Execução': 'Em Execução',
        'Em Conferência': 'Em Conferência',
        'Finalizada': 'Finalizada',
        'Cancelada': 'Cancelada',
        'Interrompida': 'Interrompida'
    };
    return statusMap[status] || status;
};

const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
        'status': 'Status',
        'atrasada': 'Atrasada',
        'status_orientacao': 'Status da Orientação',
        'status_pendencias': 'Status das Pendências',
        'pendente_de_orientacao': 'Pendente de Orientação'
    };
    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const compararDados = (dadosAntes: any, dadosDepois: any): CampoAlterado[] => {
    const campos: CampoAlterado[] = [];

    if (dadosDepois?.status && dadosAntes?.status !== dadosDepois.status) {
        campos.push({
            campo: 'status',
            antes: getStatusLabel(dadosAntes?.status || ''),
            depois: getStatusLabel(dadosDepois.status)
        });
    }

    if (dadosDepois?.atrasada !== undefined && dadosAntes?.atrasada !== dadosDepois.atrasada) {
        campos.push({
            campo: 'atrasada',
            antes: formatValue(dadosAntes?.atrasada),
            depois: formatValue(dadosDepois.atrasada)
        });
    }

    if (dadosDepois?.status_pendencias !== undefined && dadosAntes?.status_pendencias !== dadosDepois.status_pendencias) {
        campos.push({
            campo: 'status_pendencias',
            antes: formatValue(dadosAntes?.status_pendencias),
            depois: formatValue(dadosDepois.status_pendencias)
        });
    }

    return campos;
};

export default function ExpandedContentDemandaCancelada({ item }: ExpandedContentProps) {
    const [camposAlterados, setCamposAlterados] = useState<CampoAlterado[]>([]);
    const [loading, setLoading] = useState(true);
    const [justificativa, setJustificativa] = useState<string | null>(null);

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response.data.status === 'success') {
                    const logs = response.data.result || [];
                    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');

                    if (logProposta) {
                        let dadosAntes = logProposta.dados_antes;
                        let dadosDepois = logProposta.dados_depois;

                        if (typeof dadosAntes === 'string') {
                            try {
                                dadosAntes = JSON.parse(dadosAntes);
                            } catch (e) {
                                dadosAntes = null;
                            }
                        }

                        if (typeof dadosDepois === 'string') {
                            try {
                                dadosDepois = JSON.parse(dadosDepois);
                            } catch (e) {
                                dadosDepois = null;
                            }
                        }

                        const campos = compararDados(dadosAntes, dadosDepois);
                        setCamposAlterados(campos);

                        if (item.justificativa) {
                            setJustificativa(item.justificativa);
                        }
                    } else {
                        setCamposAlterados([]);
                        if (item.justificativa) {
                            setJustificativa(item.justificativa);
                        }
                    }
                } else {
                    setCamposAlterados([]);
                    setJustificativa(null);
                }
            } catch (error: any) {
                setCamposAlterados([]);
                setJustificativa(null);
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id, item.justificativa]);

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const semDados = camposAlterados.length === 0 && !justificativa;

    if (semDados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            {camposAlterados.length > 0 && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Principal</h4>
                        <TableContainer>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th className="field-col">Campo</th>
                                        <th className="before-col">Antes</th>
                                        <th className="after-col">Depois</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {camposAlterados.map((campo, index) => (
                                        <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                            <td className="field-cell">{getFieldLabel(campo.campo)}</td>
                                            <td className="before-cell">{formatValue(campo.antes)}</td>
                                            <td className="after-cell">
                                                <div className="after-content">
                                                    <span className="highlight-bar" />
                                                    <span>{formatValue(campo.depois)}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </TableContainer>
                    </div>
                </>
            )}
            {justificativa && (
                <>
                    <Separator />
                    <div className="expanded-section">
                        <h4 className="section-title">Justificativa do cancelamento</h4>
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: 'rgba(0, 0, 0, 0.02)', 
                            borderRadius: '6px',
                            color: 'black',
                            fontSize: '11px',
                            lineHeight: '20px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {justificativa}
                        </div>
                    </div>
                </>
            )}
        </ExpandedContent>
    );
}

